import { Paper, Typography } from "@mui/material";
import { Box, useMediaQuery } from "@mui/system";
import permissions from "app/permissions";
import { getPermissions } from "auth/security/securityUtils";
import CustomFilter from "features/CustomFilter";
import { useState } from "react";
import {
  List,
  Datagrid,
  TextField,
  DateField,
  ListActions,
  TextInput,
  SelectInput,
  ShowButton,
  EditButton,
  useStore,
  Title,
  SimpleList,
} from "react-admin";
import { settings } from "app";
import { MobileListRow } from "features/MobileListRow";
import SecuredComponent from "server/security/SecuredComponent";
import { checkPermission } from "server/security/utils";
import { ExamCommitteeStatusChoices } from "utils/choices";
import { MOBILE_BREAKPOINT } from "utils/constants";
import CloneExamCommitteeButton from "./CloneExamCommitteeButton";

const EXAM_COMMITEE_YEAR_STORE_KEY = "examCommitteeYear.filter";

const listFilters = [
  <TextInput
    source="name"
    label="Име на изпитна комисия"
    alwaysOn
    style={{ width: 400 }}
  />,
  <SelectInput
    source="status"
    label="Статус"
    choices={ExamCommitteeStatusChoices}
    alwaysOn
    emptyText={"Всички"}
  />,
];

const ListExamCommittiees = () => {
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT);
  // filters drives the <List> and the gate (persisted via useStore);
  // year is the draft bound to CustomFilter, committed on "Провери".
  const [filters, setFilters] = useStore(EXAM_COMMITEE_YEAR_STORE_KEY, null);
  const [year, setYear] = useState(filters?.year ?? "");

  const handleSearch = () => {
    if (year) {
      setFilters({ year });
    }
  };

  return (
    <>
      {!filters && <Title title={"Изпитни комисии"} />}
      <CustomFilter
        title="Списък изпитни комисии"
        filters={[
          {
            label: "Година",
            value: year,
            setValue: setYear,
            type: "number",
            multiple: false,
          },
        ]}
        cardWidth={"400px"}
        buttonLabel="Провери"
        buttonOnClick={handleSearch}
      />
      {!filters && (
        <Paper>
          <Box mt={3}>
            <Typography
              variant="inherit"
              align="center"
              color={"secondary"}
            >
              Въведете година за търсене на изпитна комисия.
            </Typography>
          </Box>
        </Paper>
      )}
      {filters && (
        <List
          title="Изпитни комисии"
          exporter={false}
          filters={listFilters}
          filter={filters}
          actions={
            <ListActions
              hasCreate={checkPermission(getPermissions(), permissions.canCreateSpecialistsExamCommittees)}
            />
          }
        >
          {isMobile ? (
            <SimpleList
              linkType={false}
              sx={settings.style.mobileList}
              primaryText={record => (
                <div>
                  <MobileListRow fields={[{ label: "Име на изпитна комисия", value: record?.name }]} />
                  <MobileListRow fields={[{ label: "Специалност", value: record?.specializationNameBg }]} />
                </div>
              )}
              secondaryText={record => (
                <div>
                  <MobileListRow fields={[{ label: "Статус", value: record?.statusTextBg }]} />
                  <MobileListRow fields={[{ label: "Брой членове", value: record?.membersCount }]} />
                  <MobileListRow
                    fields={[
                      {
                        label: "Създадена на",
                        value: (
                          <DateField
                            source="createdOn"
                            locales="bg-BG"
                          />
                        ),
                      },
                    ]}
                  />
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                    <SecuredComponent permission={permissions.canCreateSpecialistsExamCommittees}>
                      <CloneExamCommitteeButton />
                    </SecuredComponent>
                  </div>
                </div>
              )}
              tertiaryText={() => (
                <>
                  <SecuredComponent permission={permissions.canUpdateSpecialistsExamCommitteesInfo}>
                    <EditButton label="" />
                  </SecuredComponent>
                  <SecuredComponent permission={permissions.canPreviewSpecialistsExamCommittees}>
                    <ShowButton label="" />
                  </SecuredComponent>
                </>
              )}
            />
          ) : (
            <Datagrid
              bulkActionButtons={false}
              rowClick={false}
              sx={{
                "& .RaDatagrid-headerCell": {
                  textAlign: "left",
                },
              }}
            >
              <TextField
                source="specializationNameBg"
                label="Специалност"
                sortable={false}
              />
              <TextField
                source="name"
                label="Име на изпитна комисия"
                sortable={false}
              />
              <TextField
                source="statusTextBg"
                label="Статус"
                sortable={false}
              />
              <TextField
                source="membersCount"
                label="Брой членове"
                sortable={false}
              />
              <DateField
                source="createdOn"
                label="Създадена на"
                locales="bg-BG"
                sortable={false}
              />
              <SecuredComponent permission={permissions.canCreateSpecialistsExamCommittees}>
                <CloneExamCommitteeButton />
              </SecuredComponent>
              <SecuredComponent permission={permissions.canUpdateSpecialistsExamCommitteesInfo}>
                <EditButton />
              </SecuredComponent>
              <SecuredComponent permission={permissions.canPreviewSpecialistsExamCommittees}>
                <ShowButton />
              </SecuredComponent>
            </Datagrid>
          )}
        </List>
      )}
    </>
  );
};

export default ListExamCommittiees;
